import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import {
  TextField,
  IconButton,
  InputAdornment,
  Button as MUIButton,
  Box,
  Typography,
  Paper,
  Modal,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link, useHistory } from "react-router-dom";
import { SuccessToast, ErrorToast } from "../Toast/Toast";
import { config } from "../../Constant";
import Header from "../../layout/Header/Header";
import LoadingModal from "../../layout/LoadingModal/LoadingModal";
import { getDeviceId } from "../../utils/getDeviceId";

const Childlogin = () => {
  const history = useHistory();
  const [modalShow, setModalShow] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [deviceId, setDeviceId] = useState("");
  const [emailVerificationModal, setEmailVerificationModal] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [forgotPasswordModal, setForgotPasswordModal] = useState(false);

  const togglePassword = () => setShowPassword((prev) => !prev);

  // Load persistent device ID
  useEffect(() => {
    (async () => {
      const id = await getDeviceId();
      setDeviceId(id);
    })();
  }, []);

  const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
      .min(4, "Password must be at least 4 characters")
      .required("Password is required"),
  });

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      device_id: deviceId,
    };

    setModalShow(true);
    try {
      const res = await axios.post(`${config.url.API_URL}/nimdee/login`, payload);
      setModalShow(false);

      if (res.status === 200) {
        if (res.data.message === "Email not verified.") {
          setResendEmail(values.email);
          setEmailVerificationModal(true);
        } else {
          SuccessToast(res.data.message);
          localStorage.setItem("childDetails", JSON.stringify(res.data));
          localStorage.setItem("PopUpStreak", JSON.stringify({ popUp: true }));
          history.push("/childjourney");
        }
      } else {
        ErrorToast(res.data.message || "Login failed");
      }
    } catch (error) {
      setModalShow(false);
      const message = error.response?.data?.message;
      if (message === "Email not verified.") {
        setResendEmail(values.email);
        setEmailVerificationModal(true);
      } else {
        ErrorToast(message || "Login failed");
      }
    }
  };

  const handleResendVerification = async () => {
    try {
      const res = await axios.post(`${config.url.API_URL}/nimdee/resend-email`, { email: resendEmail });
      if (res.status === 200) {
        SuccessToast("Verification email sent successfully.");
        setEmailVerificationModal(false);
      } else {
        ErrorToast("Failed to resend verification email.");
      }
    } catch (err) {
      ErrorToast(err.response?.data?.message || "Something went wrong.");
    }
  };

  // Forgot Password Submit
  const handleForgotPassword = async (email) => {
    try {
      const res = await axios.post(`${config.url.API_URL}/nimdee/forgot-password`, { email });
      if (res.status === 200) {
        SuccessToast("Password reset link sent to your email.");
        setForgotPasswordModal(false);
      } else {
        ErrorToast("Failed to send reset link.");
      }
    } catch (err) {
      ErrorToast(err.response?.data?.message || "Something went wrong.");
    }
  };

  return (
    <>
      <Header childLoginPage="childLoginPage" />
      <LoadingModal show={modalShow} onHide={() => setModalShow(false)} />

      {/* Email Verification Modal */}
      <Modal
        open={emailVerificationModal}
        onClose={() => setEmailVerificationModal(false)}
        aria-labelledby="email-verification-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="email-verification-modal-title" variant="h6" component="h2" mb={2}>
            Email Not Verified
          </Typography>
          <Typography variant="body2" mb={3}>
            Please check your email to verify your account or click the button below to resend the verification email.
          </Typography>
          <MUIButton variant="contained" onClick={handleResendVerification}>
            Resend Verification Email
          </MUIButton>
        </Box>
      </Modal>

      {/* Forgot Password Modal */}
      <Modal
        open={forgotPasswordModal}
        onClose={() => setForgotPasswordModal(false)}
        aria-labelledby="forgot-password-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="forgot-password-modal-title" variant="h6" mb={2}>
            Reset Password
          </Typography>
          <Formik
            initialValues={{ email: "" }}
            validationSchema={Yup.object({
              email: Yup.string().email("Invalid email").required("Email is required"),
            })}
            onSubmit={(values) => handleForgotPassword(values.email)}
          >
            {({ values, handleChange, touched, errors }) => (
              <Form>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  sx={{ mb: 3 }}
                />
                <MUIButton
                  type="submit"
                  variant="contained"
                  sx={{ backgroundColor: "#ef4871", "&:hover": { backgroundColor: "#d13a60" } }}
                >
                  Submit
                </MUIButton>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>

      {/* Login Form */}
      <Paper
        elevation={3}
        sx={{
          maxWidth: 400,
          margin: "auto",
          padding: 2,
          mt: 4,
          mb:2,
          borderRadius: 3,
        }}
      >
        <Box textAlign="center" mb={3}>
          <img
            src="../../../images/login-img-lola.png"
            alt="Child Login"
            style={{ maxWidth: "100%", height: "50%", objectFit: "contain" }}
          />
        </Box>
        <Typography variant="h5" gutterBottom align="center">
          LOG IN TO YOUR ACCOUNT
        </Typography>

        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, touched, errors }) => (
            <Form>
              <Box mb={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Box>

              <Box mb={1}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={values.password}
                  onChange={handleChange}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePassword} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Forgot password link */}
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: "pointer" }}
                onClick={() => setForgotPasswordModal(true)}
              >
                Forgot Password?
              </Typography>

              <Box display="flex" justifyContent="space-between" mt={2}>
                <Link to="/" style={{ textDecoration: "none" }}>
                  <MUIButton variant="outlined" color="secondary">
                    Back
                  </MUIButton>
                </Link>
                <MUIButton
                  type="submit"
                  variant="contained"
                  sx={{ backgroundColor: "#ef4871", "&:hover": { backgroundColor: "#d13a60" } }}
                >
                  Login
                </MUIButton>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </>
  );
};

export default Childlogin;
